import React, { ReactNode } from "react";
import { Redirect } from "react-router-dom";
import { useAccessControl } from "./AccessControlContext";

interface Props {
  pageKey: string;
  /**
   * When true, only Root may view the page until Root assigns roles to it in
   * the access-control editor. When false/omitted, the page follows the default
   * (visible to all until roles are configured).
   */
  restrictedByDefault?: boolean;
  children: ReactNode;
}

/**
 * Wraps a route. Renders children only if the current user can view the page.
 * Redirects to /unauthorized otherwise. Blocks direct URL pasting for users
 * without the configured role.
 */
const PageGuard: React.FC<Props> = ({ pageKey, restrictedByDefault, children }) => {
  const { loaded, canViewPage } = useAccessControl();

  // Wait for the initial config fetch so we don't flash a redirect before
  // permissions are known.
  if (!loaded) return null;

  if (!canViewPage(pageKey, restrictedByDefault)) {
    return <Redirect to="/unauthorized" />;
  }

  return <>{children}</>;
};

export default PageGuard;
